import { useQuery, useQueryClient, UseQueryOptions } from 'react-query';

import useStore, { VerificationState } from 'app/hooks/useStore';
import { getGuestData } from 'app/services/GuestCenterService';
import { GuestData, GuestDataResponse } from 'app/services/GuestCenterService.types';
import { QueryKeys } from 'app/services/QueryClient';
import { buildPassBarcodeSet, getReservationPassBarcodes } from 'app/services/ReservationHelper';

export const queryKey = QueryKeys.GuestData;

// Helper function to create locator-specific data
export function createLocatorData(guestData: GuestData, locator: string) {
  const travelParty = guestData.travelParties?.find((tp) => tp.locator === locator);
  const travelParties = travelParty ? [travelParty] : [];
  const passes = guestData.passes?.filter((pass) => pass.locator === locator) || [];
  const passBarcodes = buildPassBarcodeSet(passes);
  const reservations =
    guestData.reservations?.filter((reservation) => {
      const reservationBarcodes = getReservationPassBarcodes(reservation);
      if (reservationBarcodes.length === 0) return false;
      return reservationBarcodes.some((barcode) => passBarcodes.has(barcode));
    }) || [];

  return {
    email: guestData.email,
    travelParties,
    passes,
    reservations,
  };
}

// Helper function to extract unique locators
export function extractLocators(guestData: GuestData): Set<string> {
  const locators = new Set<string>();
  guestData.travelParties?.forEach((tp) => {
    if (tp.locator) locators.add(tp.locator);
  });

  return locators;
}

export default function useGuestDataQuery({
  notifyOnChangeProps,
}: {
  notifyOnChangeProps?: UseQueryOptions['notifyOnChangeProps'];
} = {}) {
  const { email, verificationState, user } = useStore((s) => ({
    email: s.email,
    verificationState: s.verificationState,
    user: s.user,
  }));
  const queryClient = useQueryClient();
  const isCitypassUser = email?.endsWith('@citypass.com') || user?.email?.endsWith('@citypass.com');

  return useQuery([queryKey], () => getGuestData(useStore.getState().email), {
    enabled: !!email && verificationState === VerificationState.Verified,
    notifyOnChangeProps,
    refetchOnWindowFocus: isCitypassUser ? `always` : true,
    onSuccess: (data: GuestDataResponse | undefined) => {
      if (!data?.success || !data.guestData || !email) return;

      const { guestData } = data;
      const locators = extractLocators(guestData);

      locators.forEach((locator) => {
        const locatorGuestData = createLocatorData(guestData, locator);

        queryClient.setQueryData([QueryKeys.GuestDataByLocator, email, locator], {
          success: true,
          message: data.message,
          guestData: locatorGuestData,
          cacheData: data.cacheData,
        });
      });
    },
  });
}
